import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CarouselBlog from './CarouselBlog';

import api from '../services/api';
import './BlogList.css';
import banner11 from '../images/banner11.jpg';
import { FiClock, FiCalendar, FiBookmark, FiShare2, FiHeart } from 'react-icons/fi';

const BlogList = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [error, setError] = useState(null);
  const [sidebarSticky, setSidebarSticky] = useState(false);
  const [readingTime, setReadingTime] = useState({});

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const data = await api.get('/blogs');
        setFilteredPosts(data);

        // Calculate reading time for each post
        const timeData = {};
        data.forEach(post => {
          timeData[post.id] = calculateReadingTime(post.content);
        });
        setReadingTime(timeData);

        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchPosts();

    // Add scroll event listener for sticky sidebar
    const handleScroll = () => {
      setSidebarSticky(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const categories = ['all', ...new Set(filteredPosts.map(post => post.category))];

  const handleCategoryChange = (category) => {
    setIsLoading(true);
    setActiveCategory(category);

    setTimeout(() => {
      const filtered = category === 'all'
        ? filteredPosts
        : filteredPosts.filter(post => post.category === category);
      setFilteredPosts(filtered);
      setIsLoading(false);
    }, 300);
  };

  // const handleReload = () => {
  //   window.scrollTo(0, 0);
  // };

  const formatDate = (dateString) => {
    try {
      // Handle various date formats
      const date = dateString ? new Date(dateString) : new Date();
      if (isNaN(date.getTime())) {
        return 'No date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'No date';
    }
  };

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <>
      <CarouselBlog />

      <div className="blog-container">
        {/* Hero Banner */}
        {/* <div className="blog-hero">
        <div className="hero-content">
          <h1>Our Latest Articles</h1>
          <p>Insights, stories, and updates from our community</p>
        </div>
      </div> */}

        <div className="blog-layout">
          {/* Main Content */}
          <main className="blog-main">
            {/* Featured Post - Only show if posts exist */}
            {filteredPosts.length > 0 && (
              <div className="featured-post">
                <div className="featured-image">
                  <img
                    src={filteredPosts[0].image_url || banner11}
                    alt={filteredPosts[0].title}
                    loading="lazy"
                  />
                  <div className="featured-badge">Featured</div>
                </div>
                <div className="featured-content">
                  <div className="post-meta">
                    <span className="post-category">{filteredPosts[0].category || 'Uncategorized'}</span>
                    <span className="post-date">
                      <FiCalendar /> {formatDate(filteredPosts[0].date)}
                    </span>
                    <span className="post-time">
                      <FiClock /> {readingTime[filteredPosts[0].id] || '5'} min read
                    </span>
                  </div>
                  <h2>{filteredPosts[0].title}</h2>
                  <p className="post-excerpt">
                    {filteredPosts[0].excerpt ||
                      (filteredPosts[0].content
                        ? filteredPosts[0].content.substring(0, 200) + '...'
                        : 'No content available')}
                  </p>
                  <Link to={`/blog/${filteredPosts[0].id}`} className="read-more">
                    Continue Reading →
                  </Link>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
              </div>
            )}

            {/* Category Filters - Mobile */}
            <div className="category-filters-mobile">
              <select
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="category-select"
                disabled={isLoading}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Blog Posts Grid */}
            <div className="posts-grid">
              {filteredPosts.slice(1).map(post => (
                <article key={post.id} className="post-card">
                  <Link to={`/blog/${post.id}`} className="post-image-link">
                    <div className="post-image">
                      <img
                        src={post.image_url || banner11}
                        alt={post.title}
                        loading="lazy"
                      />
                      <div className="image-overlay"></div>
                    </div>
                  </Link>
                  <div className="post-content">
                    <div className="post-meta">
                      <span className="post-category">{post.category || 'Uncategorized'}</span>
                      <span className="post-date">
                        <FiCalendar /> {formatDate(post.date)}
                      </span>
                    </div>
                    <h3>{post.title}</h3>
                    <p className="post-excerpt">
                      {post.excerpt ||
                        (post.content
                          ? post.content.substring(0, 150) + '...'
                          : 'No content available')}
                    </p>
                    <div className="post-footer">
                      <span className="read-time">
                        <FiClock /> {readingTime[post.id] || '5'} min read
                      </span>
                      <div className="post-actions">
                        <button className="action-btn"><FiBookmark /></button>
                        <button className="action-btn"><FiShare2 /></button>
                        <button className="action-btn"><FiHeart /></button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Empty State */}
            {!isLoading && filteredPosts.length === 0 && (
              <div className="empty-state">
                <h3>No posts found</h3>
                <p>Try selecting a different category or check back later.</p>
              </div>
            )}

            {/* Pagination */}
            {filteredPosts.length > 0 && (
              <div className="pagination">
                <button className="page-btn disabled">Previous</button>
                <button className="page-btn active">1</button>
                <button className="page-btn">2</button>
                <button className="page-btn">3</button>
                <span className="page-dots">...</span>
                <button className="page-btn">8</button>
                <button className="page-btn">Next</button>
              </div>
            )}
          </main>

          {/* Sidebar */}
          <aside className={`blog-sidebar ${sidebarSticky ? 'sticky' : ''}`}>
            {/* Categories Card */}
            <div className="sidebar-card categories-card">
              <h3 className="card-title">Categories</h3>
              <div className="category-list">
                {categories.filter(cat => cat !== 'all').map(category => (
                  <button
                    key={category}
                    className={`category-item ${activeCategory === category ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category)}
                    disabled={isLoading}
                  >
                    <span className="category-name">
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </span>
                    <span className="category-count">
                      ({filteredPosts.filter(post => post.category === category).length})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Posts Card */}
            <div className="sidebar-card popular-posts">
              <h3 className="card-title">Popular Posts</h3>
              {filteredPosts.slice(0, 3).map(post => (
                <div key={`popular-${post.id}`} className="popular-post">
                  <Link to={`/blog/${post.id}`} className="popular-post-link">
                    <div className="popular-post-image">
                      <img
                        src={post.image_url || banner11}
                        alt={post.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="popular-post-content">
                      <h4>{post.title}</h4>
                      <span className="post-date">
                        <FiCalendar /> {formatDate(post.date)}
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Newsletter Card */}
            {/* <div className="sidebar-card newsletter-card">
            <h3 className="card-title">Subscribe to Newsletter</h3>
            <p>Get the latest articles and news delivered to your inbox</p>
            <form className="newsletter-form">
              <input
                type="email"
                placeholder="Your email address"
                required
                disabled={isLoading}
              />
              <button
                type="submit"
                className="subscribe-btn"
                disabled={isLoading}
              >
                Subscribe
              </button>
            </form>
          </div> */}

            {/* Tags Cloud Card */}
            <div className="sidebar-card tags-card">
              <h3 className="card-title">Tags</h3>
              <div className="tags-cloud">
                {['Technology', 'Education', 'Health', 'Business', 'Travel', 'Food', 'Lifestyle', 'Finance'].map(tag => (
                  <a key={tag} href={`/tags/${tag.toLowerCase()}`} className="tag">
                    {tag}
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default BlogList;











// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import api from '../services/api';
// import './BlogList.css';
// import banner11 from '../images/banner11.jpg';

// const BlogList = () => {
//   const [activeCategory, setActiveCategory] = useState('all');
//   const [isLoading, setIsLoading] = useState(false);
//   const [filteredPosts, setFilteredPosts] = useState([]);
//   const [activeImage, setActiveImage] = useState(null);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchPosts = async () => {
//       setIsLoading(true);
//       try {
//         const data = await api.get('/blogs');
//         setFilteredPosts(data);
//         setIsLoading(false);
//       } catch (err) {
//         setError(err.message);
//         setIsLoading(false);
//       }
//     };

//     fetchPosts();
//   }, []);

//   const categories = ['all', ...new Set(filteredPosts.map(post => post.category))];

//   const handleCategoryChange = (category) => {
//     setIsLoading(true);
//     setActiveCategory(category);

//     setTimeout(() => {
//       const filtered = category === 'all'
//         ? filteredPosts
//         : filteredPosts.filter(post => post.category === category);
//       setFilteredPosts(filtered);
//       setIsLoading(false);
//     }, 300);
//   };

//   const closeLightbox = () => {
//     setActiveImage(null);
//   };

//   const handleReload = () => {
//     window.scrollTo(0, 0);
//   };

//   if (error) return <div className="error">Error: {error}</div>;

//   return (
//     <section className="gallery-section">
//       <div className="gallery-container">
//         <div className="media-header">
//           <h2 className="gallery-title">News & Media</h2>
//           <Link to="/bloglist" className="activities-button" onClick={handleReload}>
//             View All
//           </Link>
//         </div>
//         <p className="gallery-subtitle">
//           Catch a glimpse of our activities and lectures - working humbly towards spreading the Message of Peace, Alhamdulillaah.
//         </p>

//         {/* Category Filters */}
//         <div className="gallery-filters">
//           {categories.map(category => (
//             <button
//               key={category}
//               className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
//               onClick={() => handleCategoryChange(category)}
//               disabled={isLoading}
//             >
//               {category.charAt(0).toUpperCase() + category.slice(1)}
//             </button>
//           ))}
//         </div>

//         {/* Loading Overlay */}
//         <div className="gallery-grid-container">
//           {isLoading ? (
//             <div className="grid-loading-spinner">
//               <div className="loading-spinner"></div>
//             </div>
//           ) : (
//             <div className="gallery-grid">
//               {filteredPosts.map(post => (
//                 <Link
//                   to={`/blog/${post.id}`}
//                   key={post.id}
//                   className="gallery-item-link"
//                 >
//                   <div className="gallery-item">
//                     <img
//                       src={post.image_url || banner11}
//                       alt={post.title}
//                       className="gallery-image"
//                       loading="lazy"
//                     />
//                     <div className="image-overlay">
//                       <span className="image-caption">{post.title}</span>
//                     </div>
//                   </div>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </div>

//         {/* Lightbox */}
//         {activeImage && (
//           <div className="lightbox" onClick={closeLightbox}>
//             <div className="lightbox-content" onClick={e => e.stopPropagation()}>
//               <button className="close-btn" onClick={closeLightbox}>&times;</button>
//               <img
//                 src={activeImage.image_url || banner11}
//                 alt={activeImage.title}
//                 className="lightbox-image"
//               />
//               <div className="lightbox-caption">{activeImage.title}</div>
//             </div>
//           </div>
//         )}
//       </div>
//     </section>
//   );
// };

// export default BlogList;